import { Alert, Button, Stack, TextField } from "@mui/material";
import { type FacilityUser } from "@src/appV2/ExperimentalHomeHealth/lib/types";
import { CommonDialog, LoadingButton, type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { useGetFacilityUserInternalNotes } from "./api/useGetFacilityUserInternalNotes";
import { useUpdateFacilityUserInternalNotes } from "./api/useUpdateFacilityUserInternalNotes";

interface FacilityUserInternalNotesButtonProps {
  facilityUser: FacilityUser;
  workplaceId: string;
  modalState: UseModalState;
}

export function FacilityUserInternalNotesDialog(props: FacilityUserInternalNotesButtonProps) {
  const { facilityUser, workplaceId, modalState } = props;
  const { data, isLoading, isError, isSuccess } = useGetFacilityUserInternalNotes({
    facilityUserId: facilityUser._id,
    workplaceId,
  });
  const internalNotes = data?.data;
  const [currentInternalNotes, setCurrentInternalNotes] = useState(internalNotes?.attributes.notes);

  useEffect(() => {
    setCurrentInternalNotes(internalNotes?.attributes.notes);
  }, [internalNotes]);

  const { mutateAsync: updateInternalNotes, isLoading: isUpdating } =
    useUpdateFacilityUserInternalNotes({
      meta: {
        userSuccessMessage: "Successfully updated user notes!",
        userErrorMessage: "Error updating user notes, please try again.",
      },
    });

  return (
    <CommonDialog
      modalState={modalState}
      title={`Internal notes for ${facilityUser.name}`}
      actions={
        <>
          <LoadingButton
            variant="contained"
            isLoading={isLoading || isUpdating}
            disabled={
              !isSuccess ||
              isError ||
              (currentInternalNotes ?? "") === (internalNotes?.attributes.notes ?? "")
            }
            onClick={async () => {
              await updateInternalNotes({
                facilityUserId: facilityUser._id,
                workplaceId,
                notes: currentInternalNotes ?? "",
              });

              modalState.closeModal();
            }}
          >
            Save
          </LoadingButton>
          <Button variant="outlined" onClick={modalState.closeModal}>
            Close
          </Button>
        </>
      }
    >
      <Stack spacing={2} paddingTop={1}>
        <TextField
          fullWidth
          multiline
          error={isError}
          placeholder="Enter notes here..."
          rows={3}
          disabled={isLoading || isError}
          value={currentInternalNotes}
          onChange={(event) => {
            setCurrentInternalNotes(event.target.value);
          }}
        />
        {isError && <Alert severity="error">Error loading internal notes</Alert>}
      </Stack>
    </CommonDialog>
  );
}
